import React, { useState, useEffect, useCallback } from 'react';
import { getAdminPipeline } from '../api';
import {
  ArrowLeft, CheckCircle, Warning, Clock, Brain,
  Database, Plugs, SpinnerGap, ArrowsClockwise, SealCheck
} from '@phosphor-icons/react';

const CONN_STATUS = {
  'Connected': { style: 'bg-emerald-500/15 text-emerald-700', icon: CheckCircle },
  'Degraded': { style: 'bg-amber-500/15 text-amber-700', icon: Warning },
  'Disconnected': { style: 'bg-red-500/15 text-red-700', icon: Warning },
};

const MODEL_STATUS = {
  'Active': 'bg-emerald-500/15 text-emerald-700',
  'Training': 'bg-blue-500/15 text-blue-700',
  'Inactive': 'bg-slate-400/15 text-slate-500',
};

export default function AdminPipeline({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('connections');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await getAdminPipeline();
      setData(d);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) return (
    <div className="flex items-center justify-center h-64"><SpinnerGap size={32} className="text-primary animate-spin" /></div>
  );

  const tabs = [
    { id: 'connections', label: 'OneLake Connections', count: data.connections.length },
    { id: 'data_models', label: 'Data Models', count: data.data_models.length },
    { id: 'ml_models', label: 'ML Models', count: data.ml_models.length },
  ];

  return (
    <div className="space-y-5" data-testid="admin-pipeline">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-background border border-border text-text-secondary hover:text-text-primary transition-all" data-testid="pipeline-back-btn">
          <ArrowLeft size={18} weight="bold" />
        </button>
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-extrabold text-text-primary">Live Data Pipeline</h1>
          <p className="font-body text-xs text-text-secondary mt-0.5">Manage connections, data models, and ML model registry.</p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 bg-surface border border-border rounded-xl p-1">
        {tabs.map(t => (
          <button key={t.id} data-testid={`pipeline-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-heading text-sm font-semibold transition-all ${tab === t.id ? 'bg-primary text-white' : 'text-text-secondary hover:bg-background hover:text-text-primary'}`}>
            {t.label}
            <span className={`px-1.5 py-0.5 rounded text-[10px] ${tab === t.id ? 'bg-white/20' : 'bg-border'}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Connections */}
      {tab === 'connections' && (
        <div className="space-y-3" data-testid="pipeline-connections">
          {data.connections.map(conn => {
            const st = CONN_STATUS[conn.status] || CONN_STATUS['Disconnected'];
            const StIcon = st.icon;
            return (
              <div key={conn.id} className="bg-surface border border-border rounded-xl p-5 hover:border-primary/30 transition-all" data-testid={`connection-${conn.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Plugs size={20} weight="duotone" className="text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-bold text-text-primary">{conn.name}</h3>
                      <p className="font-body text-[11px] text-text-secondary mt-0.5">{conn.type} &middot; {conn.region}</p>
                      <p className="font-body text-[10px] text-tertiary mt-1 font-mono truncate max-w-md">{conn.endpoint}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-body font-semibold ${st.style}`}>
                      <StIcon size={10} weight="fill" /> {conn.status}
                    </span>
                    <span className="text-[10px] font-body text-tertiary">{conn.tables_synced} tables synced</span>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/50">
                  <div className="flex items-center gap-1.5 text-[10px] font-body text-text-secondary">
                    <ArrowsClockwise size={11} /> Every {conn.sync_interval_min} min
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] font-body text-text-secondary">
                    <Clock size={11} /> Last: {new Date(conn.last_sync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] font-body text-text-secondary">
                    <SealCheck size={11} /> {conn.auth_method}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Data Models */}
      {tab === 'data_models' && (
        <div className="space-y-3" data-testid="pipeline-data-models">
          {data.data_models.map((dm, i) => (
            <div key={i} className="bg-surface border border-border rounded-xl p-5 hover:border-primary/30 transition-all" data-testid={`data-model-${i}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Database size={20} weight="duotone" className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-text-primary">{dm.name}</h3>
                    <p className="font-body text-xs text-text-secondary mt-0.5">{dm.description}</p>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-body font-semibold ${MODEL_STATUS[dm.status] || ''}`}>{dm.status}</span>
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/50 text-[10px] font-body text-text-secondary">
                <span>{dm.tables} tables</span>
                <span>v{dm.version}</span>
                <span>Owner: {dm.owner}</span>
                <span>Updated: {new Date(dm.last_updated).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ML Models */}
      {tab === 'ml_models' && (
        <div className="space-y-3" data-testid="pipeline-ml-models">
          {data.ml_models.map((ml, i) => (
            <div key={i} className="bg-surface border border-border rounded-xl p-5 hover:border-primary/30 transition-all" data-testid={`ml-model-${i}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                    <Brain size={20} weight="duotone" className="text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-text-primary">{ml.name}</h3>
                    <p className="font-body text-xs text-text-secondary mt-0.5">{ml.description}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {ml.input_tables.map((t, j) => (
                        <span key={j} className="px-2 py-0.5 bg-background border border-border/50 rounded text-[10px] font-body text-text-secondary font-mono">{t}</span>
                      ))}
                      <span className="text-[10px] text-tertiary mx-1">&rarr;</span>
                      <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-[10px] font-body font-medium">{ml.output}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-body font-semibold ${MODEL_STATUS[ml.status] || ''}`}>{ml.status}</span>
                  {ml.accuracy !== 'N/A' && (
                    <span className="text-[10px] font-body font-bold text-emerald-600">{ml.accuracy} acc.</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/50 text-[10px] font-body text-text-secondary">
                <span>Type: {ml.type}</span>
                <span>Last trained: {ml.last_trained !== 'N/A' ? new Date(ml.last_trained).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
